import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GlobalSearchService, SearchResult, SearchOptions, SearchResponse } from '../lib/global-search';
import { useIsMobile } from '../hooks/useMediaQuery';

interface SearchContextType {
  // Search state
  query: string;
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  
  // UI state
  isSearchOpen: boolean;
  isMobileSearchOpen: boolean;
  recentSearches: string[];
  suggestions: string[];
  isMobile: boolean;
  
  // Actions
  setQuery: (query: string) => void;
  performSearch: (options?: Partial<SearchOptions>) => Promise<void>;
  clearSearch: () => void;
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
  openMobileSearch: () => void;
  closeMobileSearch: () => void;
  addToRecentSearches: (query: string) => void;
  loadMoreResults: () => Promise<void>;
  navigateToResult: (result: SearchResult) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

const RECENT_SEARCHES_KEY = 'rulevia_recent_searches';
const MAX_RECENT_SEARCHES = 10;
const DEBOUNCE_DELAY = 300;

interface SearchProviderProps {
  children: React.ReactNode;
}

export function SearchProvider({ children }: SearchProviderProps) {
  // Search state
  const [query, setQueryState] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // UI state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  // Pagination
  const [currentOffset, setCurrentOffset] = useState(0);
  
  // Mobile detection
  const isMobile = useIsMobile();
  
  const navigate = useNavigate();
  const location = useLocation();

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const savedSearches = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (error) {
        console.error('Error loading recent searches:', error);
      }
    }
  }, []);

  // Keyboard shortcut and click outside handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd/Ctrl + K to open search (don't trigger on mobile where these don't exist)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k' && !isMobile) {
        event.preventDefault();
        openSearch();
      }
      
      // Escape to close search
      if (event.key === 'Escape') {
        if (isSearchOpen) {
          closeSearch();
        }
        if (isMobileSearchOpen) {
          closeMobileSearch();
        }
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (!isSearchOpen || isMobile) return;
      
      // Check if the clicked element is inside the search area
      const target = event.target as Element;
      if (!target) return;
      
      // Find the search container (navbar search area) and dropdown
      const searchContainer = target.closest('[data-search-container]');
      const searchDropdown = target.closest('[data-search-dropdown]');
      
      // If click is outside both search input and dropdown, close search
      if (!searchContainer && !searchDropdown) {
        // Small delay to allow other click handlers to execute first
        setTimeout(() => {
          closeSearch();
        }, 0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    if (!isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchOpen, isMobileSearchOpen, isMobile]);

  // Debounced search effect
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setTotalCount(0);
      setHasMore(false);
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(() => {
      performSearch({ query: query.trim() });
      loadSearchSuggestions(query.trim());
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [query]);

  // Load search suggestions
  const loadSearchSuggestions = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const suggestions = await GlobalSearchService.getSearchSuggestions(searchQuery, 5);
      setSuggestions(suggestions);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      setSuggestions([]);
    }
  }, []);

  // Perform search
  const performSearch = useCallback(async (options: Partial<SearchOptions> = {}) => {
    const searchQuery = options.query || query;
    
    if (!searchQuery.trim()) {
      setResults([]);
      setTotalCount(0);
      setHasMore(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const searchOptions: SearchOptions = {
        query: searchQuery.trim(),
        limit: 20,
        offset: options.offset || 0,
        ...options
      };

      const response: SearchResponse = await GlobalSearchService.search(searchOptions);
      
      // If this is a new search (offset 0), replace results
      // If it's loading more (offset > 0), append to existing results
      if (searchOptions.offset === 0) {
        setResults(response.results);
        setCurrentOffset(response.results.length);
      } else {
        setResults(prev => [...prev, ...response.results]);
        setCurrentOffset(prev => prev + response.results.length);
      }
      
      setTotalCount(response.totalCount);
      setHasMore(response.hasMore);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to perform search. Please try again.');
      setResults([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [query]);

  // Load more results
  const loadMoreResults = useCallback(async () => {
    if (!hasMore || loading) return;
    
    await performSearch({
      query,
      offset: currentOffset
    });
  }, [hasMore, loading, query, currentOffset, performSearch]);

  // Set query and clear previous results
  const setQuery = useCallback((newQuery: string) => {
    setQueryState(newQuery);
    setCurrentOffset(0);
    setError(null);
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setQueryState('');
    setResults([]);
    setTotalCount(0);
    setHasMore(false);
    setCurrentOffset(0);
    setError(null);
    setSuggestions([]);
  }, []);

  // Open search
  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  // Close search
  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    // Optionally clear selection when closing
    // setSelectedIndex(-1); // If you want to reset selection
  }, []);

  // Toggle search
  const toggleSearch = useCallback(() => {
    setIsSearchOpen(prev => !prev);
  }, []);

  // Add to recent searches
  const addToRecentSearches = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    setRecentSearches(prev => {
      const newSearches = [searchQuery, ...prev.filter(s => s !== searchQuery)]
        .slice(0, MAX_RECENT_SEARCHES);
      
      // Save to localStorage
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(newSearches));
      } catch (error) {
        console.error('Error saving recent searches:', error);
      }
      
      return newSearches;
    });
  }, []);

  // Open mobile search
  const openMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(true);
  }, []);

  // Close mobile search
  const closeMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(false);
  }, []);

  // Navigate to search result
  const navigateToResult = useCallback((result: SearchResult) => {
    navigate(result.url);
    closeSearch();
    closeMobileSearch();
    
    // Add to recent searches
    if (query.trim()) {
      addToRecentSearches(query.trim());
    }
  }, [navigate, query, addToRecentSearches, closeSearch, closeMobileSearch]);

  const value: SearchContextType = {
    // Search state
    query,
    results,
    loading,
    error,
    totalCount,
    hasMore,
    
    // UI state
    isSearchOpen,
    isMobileSearchOpen,
    recentSearches,
    suggestions,
    isMobile,
    
    // Actions
    setQuery,
    performSearch,
    clearSearch,
    openSearch,
    closeSearch,
    toggleSearch,
    openMobileSearch,
    closeMobileSearch,
    addToRecentSearches,
    loadMoreResults,
    navigateToResult
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
}

// Custom hook to use search context
export function useSearch() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}

// Export context for testing
export { SearchContext };